import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const FORM_ID = "db74qhraskivmpnrcfug";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db74qhraskivmpnrcfug";

const services = [
  {
    icon: "ri-film-line",
    title: "影视作品收录",
    desc: "收录电影、电视剧、国产剧与海外剧，涵盖最新热门作品与经典影视剧，提供封面、类型、年份、地区与评分等基础信息。",
  },
  {
    icon: "ri-file-text-line",
    title: "剧情与演员介绍",
    desc: "为每部作品整理剧情简介、特色亮点与主演阵容，帮助你快速判断一部作品是否值得一看。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯与影评",
    desc: "持续更新行业报道、深度影评与官方发布资讯，从更专业的视角了解影视圈的最新风向。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选与个性化推荐",
    desc: "支持类型、地区、年份、评分多条件筛选与排序，并通过标签匹配为你推荐相似作品。",
  },
];

const notices = [
  "本站所有影视作品与资讯内容均为演示数据，仅用于产品功能与界面展示，不构成任何真实报道或推荐。",
  "站内不提供影视播放、下载等任何资源获取功能，也不存储任何影视文件。",
  "收藏功能的数据仅保存在你当前使用的浏览器本地，清除浏览器数据后收藏将不再保留。",
  "分享功能仅生成并复制作品链接，不会向任何社交平台自动发布内容。",
  "文中配图均为示意性原创素材，如涉及版权问题请通过下方方式与我们联系。",
];

export default function About() {
  useSeo({
    title: "关于我们 - 久久爱影视内容推荐平台介绍",
    description:
      "久久爱是专注影视内容推荐与资讯的平台，提供影视作品收录、剧情介绍、演员信息、影视资讯与筛选推荐服务。在此了解我们的定位、服务范围、联系方式与版权声明。",
    keywords: "久久爱,关于我们,影视平台介绍,影视资讯服务,联系方式",
  });

  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的留言，我们已收到，会尽快回复你。");
      form.reset();
      setMessage("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      const str = String(value);
      if (key === "contact_alt" && !str.trim()) return;
      payload.append(key, str);
    });

    setStatus("loading");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFeedback("感谢你的留言，我们已收到，会尽快回复你。");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFeedback(
          serverMsg && !isSpam
            ? `提交失败：${serverMsg}`
            : "提交失败，请检查填写内容后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-4xl font-heading">
              关于久久爱
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              一个专注影视内容推荐与资讯的平台，帮你在海量作品里，找到真正想看的那一部。
            </p>
          </div>
        </div>

        <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-xl font-extrabold text-foreground-950 md:text-3xl font-heading">
                我们是谁
              </h2>
              <div className="mt-4 flex flex-col gap-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  久久爱是一个面向电影与电视剧观众的影视内容推荐平台。我们关注最新热门的影视作品，也珍视那些经得起时间检验的经典之作。
                </p>
                <p>
                  我们相信，好的作品值得被认真介绍。因此，我们没有把重点放在堆砌信息上，而是力求把每一部作品的剧情、演员与亮点讲清楚，帮助观众做出自己的判断。
                </p>
                <p>
                  无论你是想找一部周末放松的电影，还是想追一部能陪你度过无数夜晚的剧集，都希望这里能成为你探索影视世界的起点。
                </p>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 md:gap-5">
                {[
                  { value: "14+", label: "收录作品" },
                  { value: "4", label: "影视分类" },
                  { value: "6", label: "资讯专题" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg bg-primary-500 p-4 text-center"
                  >
                    <p className="text-2xl font-extrabold text-background-50 md:text-3xl font-heading">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-primary-100">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-64 w-full overflow-hidden rounded-xl bg-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20illustration%20of%20a%20cozy%20home%20cinema%20corner%20with%20a%20glowing%20screen%2C%20soft%20warm%20amber%20light%20and%20deep%20navy%20shadows%2C%20comfortable%20elegant%20composition%2C%20no%20text%2C%20premium%20editorial%20style&width=900&height=1000&seq=about-hero-01&orientation=portrait"
                alt="久久爱影视内容平台理念插图"
                title="久久爱 - 影视内容推荐平台"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto w-full px-4 pb-10 md:px-6 md:pb-14">
          <h2 className="text-xl font-extrabold text-foreground-950 md:text-3xl font-heading">
            我们提供什么
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
            {services.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-200"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full px-4 pb-10 md:px-6 md:pb-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="text-xl font-extrabold text-foreground-950 md:text-2xl font-heading">
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你有任何建议、内容纠错或合作意向，欢迎通过以下方式联系我们，或直接在右侧留言。
              </p>
              <div className="mt-5 flex flex-col gap-3 text-sm">
                <div className="flex items-start gap-3 rounded-lg bg-background-100 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">邮箱</p>
                    <p className="text-foreground-600">contact@jiujiuai.example.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg bg-background-100 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">回复时间</p>
                    <p className="text-foreground-600">工作日 09:00 - 18:00，我们会在 1-2 个工作日内回复</p>
                  </div>
                </div>
              </div>

              <h3 className="mt-8 text-base font-bold text-foreground-950">
                用户使用须知与版权声明
              </h3>
              <ul className="mt-3 flex flex-col gap-3">
                {notices.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-primary-500"></i>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="text-xl font-extrabold text-foreground-950 md:text-2xl font-heading">
                给我们留言
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                填写下面的表单，我们会尽快查看并回复你。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form={FORM_ID}
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-name" className="text-sm font-medium text-foreground-800">
                    称呼 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-email" className="text-sm font-medium text-foreground-800">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-topic" className="text-sm font-medium text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="about-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="版权问题">版权问题</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-message" className="text-sm font-medium text-foreground-800">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="请描述你的建议或问题（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  ></textarea>
                  <span className="self-end text-xs text-foreground-400">
                    {message.length} / 500
                  </span>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-guard-field"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className={`flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors ${
                    status === "loading"
                      ? "cursor-not-allowed bg-primary-400"
                      : "bg-primary-500 hover:bg-primary-600"
                  }`}
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      提交留言
                    </>
                  )}
                </button>

                {feedback && (
                  <p
                    className={`rounded-md px-3 py-2 text-sm ${
                      status === "success"
                        ? "bg-accent-100 text-accent-900"
                        : "bg-primary-100 text-primary-900"
                    }`}
                  >
                    {feedback}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}